// ─────────────────────────────────────────────────────────────────────────────
// Failed security action → something a person can read.
// Covers the user-initiated bypass / temp-disarm / arm-disarm / approval actions.
// Only error codes we actually know are translated; anything else keeps the raw
// status + code in `detail` so it stays diagnosable instead of being swallowed.
// Known codes (site-api group ②):
//   partition_disarmed    409 — bypass can only start while the partition is armed
//   bypass_already_active 409 — the camera already has an active bypass
// Approval requests return 409/410 when the request is no longer actionable.
// ─────────────────────────────────────────────────────────────────────────────

// vue-resource errors carry the parsed body on err.body.
function readCode (err) {
  const body = (err && err.body) || {}
  return body.error_code || body.err_code || body.code || null
}

function readServerMessage (err) {
  const body = (err && err.body) || {}
  const msg = body.message || body.error || body.error_message
  return typeof msg === 'string' && msg.trim() ? msg.trim() : null
}

export function describeActionError (err) {
  const status = (err && err.status) || 0
  const code = readCode(err)
  const key = ('' + (code || '')).toLowerCase()

  if (key === 'partition_disarmed') {
    return {
      title: 'Partition is disarmed',
      message: 'A bypass can only start while the partition is armed. Arm the partition first, or schedule it as a Pre-Arm bypass.',
      detail: null,
    }
  }
  if (key === 'bypass_already_active') {
    return {
      title: 'Already bypassed',
      message: 'This camera already has an active bypass. Edit the existing bypass instead of creating a new one.',
      detail: null,
    }
  }
  if (status === 403) {
    return {
      title: "You don't have permission",
      message: 'Your role does not allow this action. Contact the site owner or your dealer if you need access.',
      detail: null,
    }
  }
  if (status === 410) {
    return {
      title: 'No longer available',
      message: 'This request has expired or was already handled by someone else.',
      detail: null,
    }
  }
  if (status === 409) {
    return {
      title: "Couldn't apply the change",
      message: 'This conflicts with the current state — it may have already changed. Refresh and try again.',
      detail: code ? status + ' · ' + code : String(status),
    }
  }
  if (status === 0) {
    return {
      title: 'Network error',
      message: "Couldn't reach the server. Check your connection and try again.",
      detail: null,
    }
  }
  // Unknown → keep it honest: show what the server actually said.
  return {
    title: 'Request failed',
    message: readServerMessage(err) || 'The server rejected this request. Please try again.',
    detail: [status || null, code || null].filter(Boolean).join(' · ') || null,
  }
}
